import { Constants, GlobalConfig } from 'galarm-config'
import { ActionCreators, DateTimeUtils, WebUtils } from 'galarm-shared'
import { FirebaseProxy } from 'galarm-ps-api'
import { signOut } from 'firebase/auth'

const SessionManager = (function () {
  const addSession = (uid, sessionId) => {
    window.localStorage.setItem('sessionId', sessionId)
    const session = {
      id: sessionId,
      browser: WebUtils.getBrowserName(),
      platform: WebUtils.getDeviceOsName(),
      lastActive: DateTimeUtils.getDateTimeAsString(Date.now())
    }
    let firebaseUpdate = {}
    firebaseUpdate['userInfos/' + uid + '/sessions/' + sessionId] = session
    firebaseUpdate['userInfos/' + uid + '/activeSessionId'] = sessionId

    // Update the new session data
    GlobalConfig.firebaseHelpers.updateData(
      GlobalConfig.rootFirebaseRef,
      firebaseUpdate
    )

    addActiveTab()
    startListeningForActiveSessionChangedEvent(uid, sessionId)
  }

  function handleAppStartedOnAnotherTab(event) {
    if (event.key === 'TAB_OPEN') {
      showGalarmRunningElsewhereAlert()
      window.removeEventListener('storage', handleAppStartedOnAnotherTab)
    }
  }

  const addActiveTab = () => {
    // Add and remove the event. Addition would cause the event handler to
    // be called on any other tab that is listening on that location. Removing
    // it because otherwise TAB_OPEN key would always be present.
    window.localStorage.setItem('TAB_OPEN', true)
    window.localStorage.removeItem('TAB_OPEN')
    window.addEventListener('storage', handleAppStartedOnAnotherTab)
  }

  const showGalarmRunningElsewhereAlert = () => {
    GlobalConfig.store.dispatch(
      ActionCreators.setShowAppRunningElsewhereAlert(true)
    )
    FirebaseProxy.logEvent(
      Constants.UserAnalyticsEvents.APP_OPENED_ON_MULTIPLE_BROWSERS,
      {}
    )
  }

  let activeSessionUnsubscribe = null

  const startListeningForActiveSessionChangedEvent = (uid, sessionId) => {
    const activeSessionRef = GlobalConfig.firebaseHelpers.createRef(
      `userInfos/${uid}/activeSessionId`
    )

    activeSessionUnsubscribe = GlobalConfig.firebaseHelpers.onValue(
      activeSessionRef,
      activeSessionIdSnapshot => {
        if (activeSessionIdSnapshot.exists()) {
          const activeSessionId = activeSessionIdSnapshot.val()
          if (activeSessionId !== sessionId) {
            // If user logs in from a different window, then
            // remove the session from firebase to avoid orphan
            // sessions on Firebase
            GlobalConfig.firebaseHelpers.removeData(
              GlobalConfig.firebaseHelpers.createRef(
                `userInfos/${uid}/sessions/${sessionId}`
              )
            )
            // TODO: Do we need to unload app data here???
            showGalarmRunningElsewhereAlert()
            // Stop listening to the active session
            if (activeSessionUnsubscribe) {
              activeSessionUnsubscribe()
              activeSessionUnsubscribe = null
            }
          }
        } else {
          closeSession()
          // Stop listening to the active session
          if (activeSessionUnsubscribe) {
            activeSessionUnsubscribe()
            activeSessionUnsubscribe = null
          }
        }
      }
    )
  }

  const logOutWebSession = () => {
    // Log first because we are logged out afterwards
    FirebaseProxy.logEvent(Constants.UserAnalyticsEvents.WEB_LOG_OUT, {})
    // Stop listening to the active session
    if (activeSessionUnsubscribe) {
      activeSessionUnsubscribe()
      activeSessionUnsubscribe = null
    }
    GlobalConfig.firebaseHelpers.removeData(
      GlobalConfig.firebaseHelpers.createRef(
        `userInfos/${GlobalConfig.uid}/activeSessionId`
      )
    )
    GlobalConfig.firebaseHelpers.removeData(
      GlobalConfig.firebaseHelpers.createRef(
        `userInfos/${GlobalConfig.uid}/sessions`
      )
    )
    closeSession()
  }

  const closeSession = () => {
    GlobalConfig.store.dispatch(ActionCreators.resetApp())
    GlobalConfig.store.dispatch(ActionCreators.unloadAppData())
    window.localStorage.removeItem('uid')
    window.localStorage.removeItem('phoneId')
    window.localStorage.removeItem('sessionId')
    GlobalConfig.uid = null
    GlobalConfig.phoneId = null
    signOut(FirebaseProxy.auth())
  }

  const closeSessionForCurrentTabOnly = () => {
    // Only clean up local state - don't sign out from Firebase Auth
    // and don't remove uid, phoneId, sessionId so other tabs remain logged in
    GlobalConfig.store.dispatch(ActionCreators.resetApp())
    GlobalConfig.store.dispatch(ActionCreators.unloadAppData())
    GlobalConfig.uid = null
    GlobalConfig.phoneId = null
  }

  return {
    addSession,
    logOutWebSession,
    addActiveTab,
    closeSession,
    closeSessionForCurrentTabOnly
  }
})()

export default SessionManager
